// apps/tenant/src/lib/lab-data.ts
// Auth lives server-side: SloeAuth sign-in through this app's /auth/* routes
// and a sealed .sloelabs.com session cookie (see src/lib/sloeAuthClient.ts).
// This file keeps a thin data client for the few DIRECT browser queries
// (telemetry, architect memories). Everything else goes through /api/*.
//
// SloeData (D-086): the Lab's database is Neon behind the Neon Data API
// (PostgREST-compatible — supabase-js works unchanged, it appends /rest/v1).
// Every request carries a 5-minute SloeData token from GET /auth/data-token
// (role `authenticated`); row-level security keys on the SloeAuth subject.
// Signed out → no token → the Data API refuses (there is no anonymous role).
//
// With `accessToken` set, supabase-js turns its auth namespace OFF — "who am I"
// comes from /api/auth/me, never from this client.

import { createClient } from "@supabase/supabase-js";
import { retryingFetch } from "@sloe/sloedata";
import { getDataToken } from "./sloeAuthClient";

const RAW_URL = import.meta.env.VITE_LAB_DATA_API_URL as string | undefined;
// Accept the Data API URL with or without the /rest/v1 suffix.
const URL = RAW_URL ? RAW_URL.trim().replace(/\/+$/, "").replace(/\/rest\/v1$/, "") : undefined;

const DATA_READY = !!URL;

export const labData = DATA_READY
  ? createClient(URL!, "sloedata", {
      // The Data API ignores apikey; supabase-js insists on a non-empty string.
      accessToken: async () => (await getDataToken()) ?? "",
      // A request that reaches Postgres without its JWT identity comes back
      // 503 PT503 (Neon Data API, cold pooled connection) — retry it.
      global: { fetch: retryingFetch() },
    })
  : ({
      from: (_table: string) => ({
        insert: async (_data: any) => ({ error: null }),
        select: async () => ({ data: [], error: null }),
      }),
    } as any);

// ─── Types ───────────────────────────────────────────────────────
export interface OperatingSystem {
  id: string;
  owner_id: string | null;
  subdomain_slug: string;
  os_name: string;
  industry_shape: string;
  active_schema: unknown;
  // Public-safe brand hex from /api/os-by-slug — surfaced even pre-auth (lock
  // screen theming) while active_schema itself stays owner-gated.
  brand_color?: string | null;
  is_deployed?: boolean;
  // Server-computed in /api/os-by-slug: true only when the founder is viewing
  // the HQ OS (isFounder AND os.id === FOUNDER_HQ_OS_ID). The browser only ever
  // sees this boolean — never FOUNDER_HQ_OS_ID. Gates the Command cockpit's nav
  // + render; the real data wall is requireFounder on /api/platform/metrics.
  show_command?: boolean;
}

export interface Session {
  user: {
    id: string;
    email: string;
    user_metadata?: {
      first_name?: string;
      [key: string]: unknown;
    } | null;
  };
  // Server-computed (req.user.id === FOUNDER_USER_ID). The browser only ever
  // learns this boolean about itself — never the founder id. Gates founder-only
  // UI (the Command cockpit); it is NOT a security boundary — the wall is
  // requireFounder on /api/platform/metrics.
  is_founder?: boolean;
  // Global voice kill-switch (mirror of server VOICE_ENABLED). Boolean only —
  // the raw env value never reaches the client. Hides the voice entry points.
  voice_enabled?: boolean;
}

// ─── Data loaders ─────────────────────────────────────────────────
export async function loadOSBySlug(slug: string): Promise<OperatingSystem | null> {
  const res = await fetch(`/api/os-by-slug?slug=${encodeURIComponent(slug)}`, { credentials: "include" });
  if (!res.ok) { console.error("[loadOSBySlug] HTTP", res.status); return null; }
  const json = await res.json();
  return (json.os as OperatingSystem | null) ?? null;
}

export async function loadOSesByOwner(_userId: string): Promise<OperatingSystem[]> {
  const res = await fetch(`/api/oses-by-owner`, { credentials: "include" });
  if (!res.ok) { console.error("[loadOSesByOwner] HTTP", res.status); return []; }
  const json = await res.json();
  return (json.oses ?? []) as OperatingSystem[];
}
